import { useRouteLoaderData, useSearchParams } from '@remix-run/react';
import type { TSignupElement } from '@shared/dream-components';
import { SignupElement } from '@shared/dream-components';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import SignupAction from '~/components/SignupAction';

import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import type { RootLoaderData } from '~/root';

interface Props {
  node: TSignupElement;
  children: ReactNode | ReactNode[];
  premiumOfferId?: string;
}

const Signup = ({ node, children, premiumOfferId }: Props) => {
  const { t } = useTranslation();
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const [searchParams] = useSearchParams();
  const premiumOfferIdFromUrl = searchParams.get('premium_offer_id') || undefined;
  const isSignupModal = searchParams.get('modal') === 'signup';
  const isPremiumOfferSignup = premiumOfferIdFromUrl && isSignupModal;

  const isLoggedIn = useIsLoggedIn();
  const showWhenLoggedIn = node?.attrs?.showWhenLoggedIn;
  const hideForm = !showWhenLoggedIn && isLoggedIn && !isPremiumOfferSignup;

  if (hideForm) {
    return null;
  }

  return (
    <SignupAction
      id={node.attrs.id}
      signupFlowId={node?.attrs.signupFlowId}
      automationIds={node?.attrs.automationIds}
      premiumOfferId={premiumOfferId || premiumOfferIdFromUrl}
      redirectUrl={node?.attrs.redirectUrl}
    >
      {({ isLoading, setEmail }) => (
        <SignupElement
          element={node}
          isSubmitting={isLoading}
          isLive
          hasTermsOfService={!!site.viewable_site_version.settings.terms_of_service_enabled}
          hasCustomTermsOfService={site.viewable_site_version.settings.terms_of_service}
          onEmailChange={setEmail}
          gdprTranslations={{
            consentText: t('shared.cta.gdpr_signup_consent') || 'I consent to receive newsletters via email.',
            termsOfUseText: t('footer.terms_of_use') || 'Terms of Use',
            privacyPolicyText: t('footer.privacy_policy') || 'Privacy Policy',
            andText: t('shared.cta.and') || 'and',
            consentTextVariantTwo:
              `${t('shared.cta.gdpr_signup_consent')} ${t('shared.cta.sign_up')}` ||
              'I consent to receive newsletters via Email. Signup',
            termsOfServiceText: t('meta.titles.tos') || 'Terms of Service',
          }}
        >
          {children}
        </SignupElement>
      )}
    </SignupAction>
  );
};

export default Signup;
